import React, { useState, useEffect, useCallback } from 'react';

const C = {
  primary: '#0F6E56', primaryBg: '#E1F5EE', border: '#E3E1D9',
  text: '#1F1E1B', muted: '#5F5E5A', surface: '#F7F5F0',
  gold: '#91600C', danger: '#C0392B',
};

function authHeaders() {
  const t = localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Token ${t}` } : {}) };
}

const inp = {
  width: '100%', padding: '9px 11px', fontSize: 13.5, border: `1px solid ${C.border}`,
  borderRadius: 7, boxSizing: 'border-box', marginBottom: 8,
};
const card = {
  background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12,
  padding: '1rem', marginBottom: '1rem',
};
const title = { fontSize: 15, fontWeight: 700, margin: '0 0 4px', color: C.text };
const btn = {
  fontSize: 13, fontWeight: 700, padding: '9px 18px', border: 'none',
  borderRadius: 8, background: C.primary, color: '#fff', cursor: 'pointer',
};

/* ==================== PROFILE COMPLETION ==================== */
export function CompletionCard() {
  const [d, setD] = useState(null);

  const load = useCallback(() => {
    fetch('/api/my-completion/', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && !j.error) setD(j); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  const color = d.percent >= 100 ? C.primary : d.percent >= 60 ? C.gold : C.danger;

  return (
    <div style={card}>
      <h3 style={title}>{'\u{1F464}'} Profile {d.percent}% complete</h3>
      <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 8px' }}>
        {d.done} of {d.total} done
        {d.percent < d.admin_min_completion
          ? ` · ${d.admin_min_completion}% needed to apply for an admin role`
          : ' · you can apply for an admin role'}
      </p>

      <div style={{ background: C.surface, borderRadius: 5, height: 8, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ width: `${d.percent}%`, height: '100%', background: color, transition: 'width .4s' }} />
      </div>

      {d.items.map((i) => (
        <div key={i.key} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '5px 0', borderBottom: `1px solid ${C.surface}`,
        }}>
          <span style={{ fontSize: 12.5, color: i.done ? C.muted : C.text, fontWeight: i.done ? 400 : 600 }}>
            {i.done ? '\u2705' : '\u25CB'} {i.icon} {i.label}
          </span>
          <span style={{ fontSize: 10.5, color: C.muted, textAlign: 'right', maxWidth: '52%' }}>
            {i.done ? '' : i.why}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ==================== NEXT OF KIN ==================== */
export function NextOfKinCard() {
  const [f, setF] = useState({ name: '', relation: '', phone: '', email: '', wallet: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch('/api/my-next-of-kin/', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && !j.error) setF({
        name: j.name || '', relation: j.relation || '', phone: j.phone || '',
        email: j.email || '', wallet: j.wallet || '', note: j.note || '',
      }); })
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!f.name.trim()) { setMsg('Please enter a name.'); return; }
    if (!f.phone.trim() && !f.email.trim()) { setMsg('Please add a phone number or an email.'); return; }
    setBusy(true); setMsg('');
    try {
      const res = await fetch('/api/my-next-of-kin/', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(f),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Could not save.');
      setMsg(`Saved. Your profile is now ${j.percent}% complete.`);
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  return (
    <div style={card}>
      <h3 style={title}>{'\u{1F46A}'} Next of kin</h3>
      <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 10px', lineHeight: 1.5 }}>
        If something happens to you, this is the person we contact about your balance
        and any unfinished orders. Only admins can see this.
      </p>

      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
        placeholder="Full name" style={inp} />
      <input value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })}
        placeholder="Relationship (brother, wife, son…)" style={inp} />
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
          placeholder="Phone" style={inp} />
        <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
          placeholder="Email" style={inp} />
      </div>
      <input value={f.wallet} onChange={(e) => setF({ ...f, wallet: e.target.value })}
        placeholder="Their Pi wallet address (optional)" style={inp} />
      <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
        placeholder="Anything we should know (optional)" style={inp} />

      {msg && (
        <p style={{ fontSize: 12, margin: '0 0 8px',
                    color: msg.startsWith('Saved') ? C.primary : C.danger }}>{msg}</p>
      )}
      <button onClick={save} disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Saving…' : 'Save next of kin'}
      </button>
    </div>
  );
}

/* ==================== ADMIN APPLY ==================== */
export function AdminApplyCard() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ module: '', rights: [], why: '', experience: '', hours: 5 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch('/api/admin-apply/', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && !j.error) setD(j); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;

  const send = async () => {
    if (!f.module) { setMsg('Please choose a module.'); return; }
    if (f.why.trim().length < 20) { setMsg('Please tell us why in a bit more detail.'); return; }
    setBusy(true); setMsg('');
    try {
      const res = await fetch('/api/admin-apply/', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          module: f.module, rights_requested: f.rights,
          why: f.why, experience: f.experience,
          hours_per_week: parseInt(f.hours, 10) || 0,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Could not send.');
      setMsg('Application sent. You will hear back soon.');
      setOpen(false);
      setF({ module: '', rights: [], why: '', experience: '', hours: 5 });
      load();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  const toggleRight = (k) =>
    setF({ ...f, rights: f.rights.includes(k) ? f.rights.filter((x) => x !== k) : [...f.rights, k] });

  return (
    <div style={card}>
      <h3 style={title}>{'\u{1F6E1}\uFE0F'} Become a moderator</h3>
      <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 10px', lineHeight: 1.5 }}>
        Help run one part of PenFlow — approve listings, sort out disputes, keep things clean.
        You only get the powers you are given, and only for that module.
      </p>

      {d.my_applications.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {d.my_applications.map((a) => (
            <div key={a.id} style={{
              background: C.surface, borderRadius: 7, padding: '7px 10px', marginBottom: 5,
              display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{a.module}</span>
              <span style={{ fontSize: 11, fontWeight: 700,
                             color: a.status === 'approved' ? C.primary
                                  : a.status === 'rejected' ? C.danger : C.gold }}>
                {a.status}
              </span>
              {a.note && <p style={{ margin: 0, fontSize: 11, color: C.muted, width: '100%' }}>{a.note}</p>}
            </div>
          ))}
        </div>
      )}

      {!d.eligible ? (
        <div style={{ background: '#FDF0DD', borderRadius: 7, padding: '8px 11px' }}>
          <p style={{ margin: 0, fontSize: 12, color: C.gold, fontWeight: 600 }}>
            Your profile is {d.my_completion}% complete — {d.required_completion}% is needed.
          </p>
          {d.missing.length > 0 && (
            <p style={{ margin: '3px 0 0', fontSize: 11, color: C.muted }}>
              Still needed: {d.missing.join(', ')}
            </p>
          )}
        </div>
      ) : !open ? (
        <button onClick={() => setOpen(true)} style={btn}>Apply</button>
      ) : (
        <>
          <select value={f.module} onChange={(e) => setF({ ...f, module: e.target.value })} style={inp}>
            <option value="">Which part of PenFlow?</option>
            {d.available_modules.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>

          <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 5px' }}>
            What would you like to be able to do?
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 9 }}>
            {d.available_rights.map((r) => {
              const on = f.rights.includes(r.key);
              return (
                <button key={r.key} onClick={() => toggleRight(r.key)} title={r.label}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 7, cursor: 'pointer',
                    border: `1px solid ${on ? C.primary : C.border}`,
                    background: on ? C.primaryBg : '#fff',
                    color: on ? C.primary : C.muted, fontWeight: on ? 700 : 400,
                  }}>
                  {on ? '\u2713 ' : ''}{r.label}
                </button>
              );
            })}
          </div>

          <textarea value={f.why} onChange={(e) => setF({ ...f, why: e.target.value })} rows={3}
            placeholder="Why do you want to help with this part?" style={{ ...inp, resize: 'vertical' }} />
          <textarea value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} rows={2}
            placeholder="Any relevant experience? (optional)" style={{ ...inp, resize: 'vertical' }} />
          <input value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} type="number"
            placeholder="Hours per week you can give" style={inp} />

          {msg && <p style={{ fontSize: 12, color: C.danger, margin: '0 0 8px' }}>{msg}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={send} disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Sending…' : 'Send application'}
            </button>
            <button onClick={() => { setOpen(false); setMsg(''); }}
              style={{ ...btn, background: '#fff', color: C.muted, border: `1px solid ${C.border}` }}>
              Cancel
            </button>
          </div>
        </>
      )}

      {msg && !open && (
        <p style={{ fontSize: 12, margin: '8px 0 0',
                    color: msg.startsWith('Application') ? C.primary : C.danger }}>{msg}</p>
      )}
    </div>
  );
}
